import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import {
  RecoveryLivenessService,
  type LivenessResult,
  type LivenessCredentials,
} from './recovery-liveness.service';
import { describeError, type DescribedError } from './recovery-error';

type Phase = 'upload' | 'starting' | 'liveness' | 'checking' | 'result' | 'error';

/**
 * POC page for account recovery via face liveness.
 *
 * Flow: upload a document holding the reference face -> mint a Rekognition
 * liveness session + short-lived scoped AWS creds -> run the managed liveness
 * widget (React island) -> ask the backend to compare the live frame against the
 * document face. The page only displays the verdict; no session is ever issued.
 */
@Component({
  selector: 'app-recovery-test',
  templateUrl: './recovery-test.component.html',
  styleUrls: ['./recovery-test.component.scss'],
})
export class RecoveryTestComponent implements OnDestroy {
  @ViewChild('livenessHost') livenessHost?: ElementRef<HTMLDivElement>;

  phase: Phase = 'upload';
  documentImage: string | null = null; // base64 payload, no data-uri prefix
  documentPreview: string | null = null;
  result: LivenessResult | null = null;
  error: DescribedError | null = null;

  private unmount?: (el: HTMLElement) => void;

  constructor(private svc: RecoveryLivenessService) {}

  onFile(ev: Event): void {
    const file = (ev.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUri = String(reader.result);
      this.documentPreview = dataUri;
      this.documentImage = dataUri.split(',')[1] ?? null;
    };
    reader.onerror = () => this.fail(new Error('Could not read that file'), 'read the document file');
    reader.readAsDataURL(file);
  }

  async start(): Promise<void> {
    if (!this.documentImage) return;
    this.phase = 'starting';

    // Run these sequentially rather than in parallel: with Promise.all a rejection
    // tells you nothing about WHICH call failed, which is exactly what you need here.
    let sessionId: string;
    try {
      sessionId = (await this.svc.createSession()).sessionId;
    } catch (e) {
      return this.fail(e, 'mint a liveness session (POST /recovery-liveness-session)');
    }

    let creds: LivenessCredentials;
    try {
      creds = await this.svc.getCredentials();
    } catch (e) {
      return this.fail(e, 'fetch liveness credentials (POST /recovery-liveness-credentials)');
    }

    let island: typeof import('./face-liveness-island');
    try {
      // Lazy-load the island so React/Amplify never enter the main bundle.
      island = await import('./face-liveness-island');
    } catch (e) {
      return this.fail(e, 'load the liveness widget bundle');
    }
    this.unmount = island.unmountFaceLiveness;

    this.phase = 'liveness';

    // Wait a tick so *ngIf renders the host element before React mounts into it.
    setTimeout(() => {
      const host = this.livenessHost?.nativeElement;
      if (!host) {
        this.fail(new Error('Liveness container element was not rendered'), 'mount the liveness widget');
        return;
      }

      try {
        island.mountFaceLiveness(host, {
          sessionId,
          region: creds.region,
          credentials: creds,
          onComplete: () => void this.finish(sessionId),
          onError: (e) => this.fail(e, 'run the liveness check'),
        });
      } catch (e) {
        this.fail(e, 'mount the liveness widget');
      }
    });
  }

  private async finish(sessionId: string): Promise<void> {
    this.phase = 'checking';
    this.teardown();

    try {
      this.result = await this.svc.getResult(sessionId, this.documentImage!);
      this.phase = 'result';
    } catch (e) {
      this.fail(e, 'compare your face against the document (POST /recovery-liveness-result)');
    }
  }

  private fail(e: unknown, step: string): void {
    this.teardown();
    this.error = describeError(e, step);
    this.phase = 'error';
    // Keep the raw object in devtools — the on-screen text is a summary, not the whole truth.
    console.error('[recovery-test] failed to ' + step, e);
  }

  private teardown(): void {
    const host = this.livenessHost?.nativeElement;
    if (host && this.unmount) this.unmount(host);
  }

  reset(): void {
    this.teardown();
    this.phase = 'upload';
    this.result = null;
    this.error = null;
    this.documentImage = null;
    this.documentPreview = null;
  }

  ngOnDestroy(): void {
    this.teardown();
  }
}
